import type { ThemeConfig } from 'antd';
import isNil from 'lodash/isNil';
import mergeWith from 'lodash/mergeWith';

const lightIconColor = 'rgb(247, 148, 37)';
const darkIconColor = 'rgb(47,157,255)';
const darkCardColor = 'rgb(20, 27, 44)';

const darkBackgroundColor = '#010716';
const darkBorderColor = 'rgb(147, 147,147)';
const darkPlaceholderText = 'rgba(200,203,200,0.81)';

const primaryColor = 'rgb(0, 29, 102)';
export const warningGradientColor =
  'linear-gradient(180deg, rgba(176, 94, 2, 0.30) 0%, rgba(255, 255, 255, 0.30) 79.33%)';
export const successGradientColor =
  'linear-gradient(180deg, rgba(28, 99, 128, 0.30) 0%, rgba(255, 255, 255, 0.30) 79.33%)';

// Keep theme config seperate as we can export this into antd's theme editor
const themeConfig: ThemeConfig = {
  token: {
    colorPrimary: primaryColor,
    colorInfo: '#1677ff',
    colorLink: '#004db9',
    colorTextDisabled: '#535353',
    colorPrimaryBg: '#e6f4ff',
    colorPrimaryBgHover: '#bae0ff',
    colorPrimaryBorder: '#91caff',
    colorPrimaryBorderHover: '#69b1ff',
    colorPrimaryHover: '#4096ff',
    colorPrimaryActive: '#0958d9',
    colorPrimaryTextHover: '#003eb3',
    colorPrimaryTextActive: primaryColor,
    colorPrimaryText: '#002c8c',
    colorSuccessTextActive: '#092b00',
    colorSuccessText: '#135200',
    colorSuccessTextHover: '#237804',
    colorSuccessActive: '#389e0d',
    colorSuccessHover: '#73d13d',
    colorWarning: '#faad14',
    colorWarningText: '#7f4d01ff',
    colorWarningTextHover: '#874d00',
    colorWarningTextActive: '#613400',
    colorError: '#a8071a',
    orange: 'rgb(212,107,8)',
    blue: 'rgb(0,64,201)', // Generates palette which is compliant for light / dark mode
    green: 'rgb(51,182,3)', // Generates palette which is compliant for light / dark mode
    red: 'rgb(191,8,30)', // Generates palette which is compliant for light / dark mode
  },
  components: {
    Button: {
      defaultBorderColor: '#002c8c',
      defaultColor: '#002c8c',
      colorTextDisabled: '#535353',
      algorithm: true,
      primaryShadow: '0',
      dangerShadow: '0',
    },
    Dropdown: {
      colorText: 'black',
      colorTextDisabled: '#595959',
    },
    Layout: {
      bodyBg: 'rgb(230,239,249)',
      lightSiderBg: 'rgb(0,29,102)',
      siderBg: 'rgb(0,29,102)',
      footerBg: 'transparent',
      headerBg: 'white',
    },
    Menu: {
      itemBg: 'rgb(0,29,102)',
      itemColor: 'rgb(255,255,255)',
      itemHoverBg: 'rgb(1,18,62)',
      itemHoverColor: 'rgb(255,255,255)',
      subMenuItemBg: 'rgb(1,25,86)',
      itemSelectedBg: 'rgb(1,18,62)',
      itemSelectedColor: 'rgb(255,255,255)',
      subMenuItemSelectedColor: 'rgb(255,255,255)',
      colorSplit: 'rgb(255,255,255)',
      groupTitleColor: 'rgb(255,255,255)',
      itemActiveBg: 'rgb(0,11,38)',
      itemDisabledColor: 'rgba(191,190,190,0.74)',
      algorithm: true,
    },
    Card: {
      boxShadow: '0 4px 14px 0 rgba(220, 225, 232, 0.5)',
      extraColor: lightIconColor,
    },
    Descriptions: {
      itemPaddingBottom: 8,
      itemPaddingEnd: 10,
      labelColor: 'black',
      algorithm: true,
    },
    Pagination: {
      algorithm: true,
    },
    Segmented: {
      itemColor: primaryColor,
    },
    Slider: {
      trackBg: primaryColor,
      handleColor: primaryColor,
    },
    Tabs: {
      itemSelectedColor: '#001d66',
    },
    Tag: {
      defaultBg: 'white',
      algorithm: true,
    },
  },
};

const darkThemeConfig: ThemeConfig = {
  token: {
    colorBorder: darkBorderColor,
    colorLink: '#a6caff',
    colorLinkHover: '#71aaff',
    blue: '#0091ff',
    colorError: '#ff4d4f',
    colorErrorBg: '#ff0020',
  },
  components: {
    Button: {
      defaultBorderColor: '#60adf7',
      defaultColor: '#60adf7',
      defaultBg: darkCardColor,
      defaultHoverBg: 'rgb(13, 20, 39)',
      defaultHoverColor: darkIconColor,
      colorLink: '#a6caff',
      colorLinkHover: '#71aaff',
      algorithm: true,
    },
    Breadcrumb: {
      itemColor: 'rgba(239,239,239,0.63)',
      lastItemColor: 'white',
    },
    Card: {
      boxShadow: 'rgb(0 0 0 / 11%) 0px 4px 14px 0px',
      extraColor: darkIconColor,
      colorBgContainer: darkCardColor,
    },
    Checkbox: {
      colorBgContainer: darkCardColor,
    },
    DatePicker: {
      colorBgContainer: darkCardColor,
      colorBgElevated: darkCardColor,
      colorTextPlaceholder: darkPlaceholderText,
    },
    Descriptions: {
      labelColor: 'white',
    },
    Drawer: {
      colorBgElevated: darkCardColor,
    },
    Dropdown: {
      colorBgElevated: darkCardColor,
    },
    Input: {
      colorBgContainer: darkCardColor,
      colorTextPlaceholder: darkPlaceholderText,
    },
    InputNumber: {
      colorBgContainer: darkCardColor,
      colorTextPlaceholder: darkPlaceholderText,
    },
    Layout: {
      bodyBg: '#010716',
      headerBg: 'black',
    },
    Modal: {
      contentBg: darkCardColor,
      headerBg: darkCardColor,
    },
    Pagination: {
      colorPrimary: darkIconColor,
      colorBgContainer: darkCardColor,
    },
    Segmented: {
      itemSelectedBg: darkCardColor,
      trackBg: darkIconColor,
    },
    Select: {
      colorBgContainer: darkCardColor,
      colorBgElevated: darkCardColor,
      optionSelectedBg: darkBackgroundColor,
      colorTextPlaceholder: darkPlaceholderText,
    },
    Spin: {
      colorPrimary: 'rgb(0,82,182)',
    },
    Radio: {
      colorBgContainer: darkCardColor,
      colorPrimary: darkIconColor,
    },
    Table: {
      rowSelectedBg: 'rgba(46, 155, 244, 0.72)',
      rowSelectedHoverBg: 'rgb(0, 77, 170)',
      colorBgContainer: darkCardColor,
      borderColor: 'rgb(83, 83, 83)',
      headerSplitColor: 'rgb(83, 83, 83)',
      algorithm: true,
    },
    Tabs: {
      itemSelectedColor: darkIconColor,
      inkBarColor: darkIconColor,
    },
    Tag: {
      defaultBg: '#212327b3',
      algorithm: true,
    },
  },
};

export const defaultTheme = (darkMode?: boolean): ThemeConfig =>
  darkMode
    ? mergeWith({}, themeConfig, darkThemeConfig, (objValue, srcValue) =>
        isNil(srcValue) ? objValue : undefined
      )
    : themeConfig;
